import React, { useState } from 'react';
import { TrendingUp } from 'lucide-react';
import { mockData } from '../mock';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const ResultsSection = () => {
  const { results } = mockData;
  const [headerRef, headerVisible] = useScrollAnimation();
  const [graph1Ref, graph1Visible] = useScrollAnimation();
  const [graph2Ref, graph2Visible] = useScrollAnimation();
  const [graph3Ref, graph3Visible] = useScrollAnimation();
  const [imageErrors, setImageErrors] = useState({});

  // Helper function to get animation class based on index
  const getAnimationClass = (index) => {
    if (index === 0) return 'slide-in-left';
    if (index === 2) return 'slide-in-right';
    return 'fade-in-bottom';
  };

  return (
    <section id="results" className="relative py-12 md:py-24 bg-gradient-to-b from-black to-gray-900">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/2 left-0 w-64 h-64 bg-[#FFE7AB]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-[#5F9BC7]/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 container mx-auto px-4">
        {/* Section Header */}
        <div 
          ref={headerRef}
          className={`scroll-animate fade-in-bottom ${headerVisible ? 'visible' : ''} text-center mb-16`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFE7AB]/10 border border-[#FFE7AB]/30 backdrop-blur-sm mb-4">
            <TrendingUp className="w-4 h-4 text-[#FFE7AB]" />
            <span className="text-sm font-medium text-[#FFE7AB]">Proven Results</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {results.title}
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            {results.subtitle}
          </p>
        </div>

        {/* Results Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {results.clientResults.map((result, index) => {
            const animRefs = [graph1Ref, graph2Ref, graph3Ref];
            const animVisible = [graph1Visible, graph2Visible, graph3Visible];
            
            return (
              <div 
                key={result.id}
                ref={animRefs[index]}
                className={`scroll-animate ${getAnimationClass(index)} ${animVisible[index] ? 'visible' : ''} group relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-[#5F9BC7]/50 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#5F9BC7]/20`}
              >
                {/* Graph Image - Full Frame */}
                <div className="relative h-96 overflow-hidden">
                  {imageErrors[result.id] ? (
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-500 bg-gradient-to-br from-gray-900 to-black">
                      <svg className="w-20 h-20 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <p className="text-sm">Progress Graph {index + 1}</p>
                      <p className="text-xs mt-2 px-4 text-center">Add your client's weight loss graph to /app/frontend/public/images/graph{index + 1}.jpg</p>
                    </div>
                  ) : (
                    <img 
                      src={result.graphImage} 
                      alt={`Client progress graph ${index + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={() => setImageErrors(prev => ({ ...prev, [result.id]: true }))}
                    />
                  )}
                </div>

                {/* Testimonial */}
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="w-5 h-5 text-[#5F9BC7]" />
                    <div className="h-1 w-12 bg-gradient-to-r from-[#5F9BC7] to-[#FFE7AB] rounded-full"></div>
                  </div>
                  <p className="text-gray-300 leading-relaxed italic">"{result.testimonial}"</p>
                </div>

                {/* Hover Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#5F9BC7]/0 to-[#5F9BC7]/0 group-hover:from-[#5F9BC7]/10 group-hover:to-transparent transition-all duration-300 pointer-events-none"></div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ResultsSection;
